import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { attendanceRegularizationApi } from '../api/attendanceRegularizationApi';
import { appToast } from '../../../../../components/AppToast';

export function useAttendanceRegularizationEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');

  const employeeInfo = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        name: stored.name || stored.userName || 'Divyanshu Singh',
        empId: stored.employee_id || stored.empCode || '348414',
      };
    } catch {
      return {
        name: 'Divyanshu Singh',
        empId: '348414',
      };
    }
  }, []);

  const [name, setName] = useState(employeeInfo.name);
  const [date, setDate] = useState('2026-08-31');
  const [timeHour, setTimeHour] = useState('01');
  const [timeMinute, setTimeMinute] = useState('00');
  const [timeAmPm, setTimeAmPm] = useState('am');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [gpsIn, setGpsIn] = useState('28.6139° N, 77.2090° E (Current Location)');
  const [isGpsOpen, setIsGpsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const reasonOptions = [
    { value: '', label: 'Select Reason' },
    { value: 'Missed In-Punch', label: 'Missed In-Punch' },
    { value: 'Missed Out-Punch', label: 'Missed Out-Punch' },
    { value: 'Work from Home', label: 'Work from Home' },
    { value: 'On Duty / Client Visit', label: 'On Duty / Client Visit' },
    { value: 'Technical Issue', label: 'Technical Issue' },
    { value: 'Others', label: 'Others' },
  ];

  const hourOptions = Array.from({ length: 12 }, (_, i) => {
    const val = String(i + 1).padStart(2, '0');
    return { value: val, label: val };
  });

  const minuteOptions = Array.from({ length: 60 }, (_, i) => {
    const val = String(i).padStart(2, '0');
    return { value: val, label: val };
  });

  const amPmOptions = [
    { value: 'am', label: 'AM' },
    { value: 'pm', label: 'PM' },
  ];

  // Load record for edit if id exists
  useEffect(() => {
    if (!editId) return;
    const fetchRecord = async () => {
      const record = await attendanceRegularizationApi.getById(editId);
      if (record) {
        setName(record.name || employeeInfo.name);
        setDate(record.date || '2026-08-31');
        setTimeHour(record.timeHour || '01');
        setTimeMinute(record.timeMinute || '00');
        setTimeAmPm(record.timeAmPm || 'am');
        setReason(record.reason || '');
        setRemarks(record.remarks || '');
        if (record.gpsIn) setGpsIn(record.gpsIn);
      }
    };
    fetchRecord();
  }, [editId, employeeInfo.name]);

  const validate = () => {
    if (!reason) {
      appToast.error('Please select a regularization reason');
      return false;
    }
    if (!remarks.trim()) {
      appToast.error('Please provide brief remarks explaining the reason');
      return false;
    }
    return true;
  };

  const handleSaveAndExit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = {
        name,
        empId: employeeInfo.empId,
        date,
        timeHour,
        timeMinute,
        timeAmPm,
        reason,
        remarks,
        gpsIn,
      };

      if (editId) {
        await attendanceRegularizationApi.update(editId, payload);
        appToast.success('Regularization entry updated successfully');
      } else {
        await attendanceRegularizationApi.create(payload);
        appToast.success('Regularization entry submitted successfully');
      }

      navigate('/attendance/regularization');
    } catch {
      appToast.error('Failed to save regularization request');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndContinue = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = {
        name,
        empId: employeeInfo.empId,
        date,
        timeHour,
        timeMinute,
        timeAmPm,
        reason,
        remarks,
        gpsIn,
      };

      if (editId) {
        await attendanceRegularizationApi.update(editId, payload);
        appToast.success('Regularization entry updated successfully');
      } else {
        await attendanceRegularizationApi.create(payload);
        appToast.success('Regularization entry submitted successfully');
      }

      // Reset fields for new entry
      setReason('');
      setRemarks('');
      setTimeHour('01');
      setTimeMinute('00');
      setTimeAmPm('am');
    } catch {
      appToast.error('Failed to save regularization request');
    } finally {
      setIsSaving(false);
    }
  };

  const mode = searchParams.get('mode') || (editId ? 'edit' : 'new');
  const isView = mode === 'view';

  const handleCancel = () => {
    navigate('/attendance/attendance-regularization');
  };

  return {
    state: {
      isEdit: Boolean(editId) && !isView,
      isView,
      mode,
      editId,
      name,
      date,
      timeHour,
      timeMinute,
      timeAmPm,
      reason,
      remarks,
      gpsIn,
      isGpsOpen,
      isSaving,
      reasonOptions,
      hourOptions,
      minuteOptions,
      amPmOptions,
    },
    actions: {
      setName,
      setDate,
      setTimeHour,
      setTimeMinute,
      setTimeAmPm,
      setReason,
      setRemarks,
      setGpsIn,
      setIsGpsOpen,
      handleSaveAndExit,
      handleSaveAndContinue,
      handleCancel,
    },
  };
}
